import React, { useState, useEffect } from 'react';
// eslint-disable-next-line
import { IconButton, Box, Typography, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Close as CloseIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import menuIcon from '../img/menu.svg';

// Les fresques sont l'entree principale du menu : on y va en un clic, sans
// page intermediaire. Les libelles viennent des memes cles i18n qu'avant.
const FRESCO_CARDS = [
  {
    to: '/littlemermaid',
    image: '/assets/frescoes/bw-thumbnail.jpg',
    titleKey: 'frescoes_page.left_title',
    subtitleKey: 'frescoes_page.left_subtitle',
    altKey: 'frescoes_page.left_image_alt',
  },
  {
    to: '/imieiamori',
    image: '/assets/frescoes/color-thumbnail.jpg',
    titleKey: 'frescoes_page.right_title',
    subtitleKey: 'frescoes_page.right_subtitle',
    altKey: 'frescoes_page.right_image_alt',
  },
];

const LINKS = [
  { labelKey: 'about', to: '/about' },
  { labelKey: 'Flowers of Joy', to: '/flowers' },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(0);
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  // Hauteur reellement visible : sur Safari iOS, 100% et 100vh passent sous la
  // barre d'outils et le bas du menu se retrouve coupe.
  useEffect(() => {
    const measure = () => {
      const vv = window.visualViewport;
      setViewportHeight(vv ? vv.height : window.innerHeight);
    };
    measure();

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', measure);
      vv.addEventListener('scroll', measure);
    }
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', measure);
        vv.removeEventListener('scroll', measure);
      }
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  // Le menu couvre l'ecran : on bloque le scroll de la page derriere lui,
  // sinon le fond defile sous les doigts sur mobile.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  // Echap ferme le menu, comme la croix.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  // Espace restant pour les deux images une fois deduits le header, la rangee
  // de liens, les legendes et les marges. Evite que la seconde carte soit
  // coupee en bas sur les ecrans courts.
  const RESERVED_ABOVE_AND_BELOW = 300;
  const cardMaxHeight = viewportHeight
    ? Math.max(110, Math.floor((viewportHeight - RESERVED_ABOVE_AND_BELOW) / 2))
    : 0;

  const handleMenuToggle = () => setMenuOpen((open) => !open);

  const goTo = (to) => {
    navigate(to);
    setMenuOpen(false);
  };

  // eslint-disable-next-line
  const handleChangeLanguage = (event) => {
    const selectedLanguage = event.target.value;
    i18n.changeLanguage(selectedLanguage);
    localStorage.setItem('language', selectedLanguage);
  };

  return (
    <Box component="header" sx={{ position: 'relative', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          top: 0,
          width: '100%',
          position: 'fixed',
          zIndex: 10,
          py: 1,
          backgroundColor: '#ffffff',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Ranade, sans-serif',
            fontWeight: 300,
            letterSpacing: '0.02em',
            color: '#000000',
            ml: 2,
            cursor: 'pointer',
          }}
          onClick={() => goTo('/')}
        >
          KAJIKA AKI
        </Typography>

        <IconButton
          aria-label="menu"
          onClick={handleMenuToggle}
          sx={{ color: '#000000', mr: 1, height: '40px', width: '40px' }}
        >
          {menuOpen ? (
            <CloseIcon sx={{ fontSize: '22px' }} />
          ) : (
            <img src={menuIcon} alt="Menu" style={{ height: '26px' }} />
          )}
        </IconButton>
      </Box>

      {/* Menu en plein écran */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: viewportHeight ? `${viewportHeight}px` : '100%',
              backgroundColor: '#ffffff',
              zIndex: 9,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
          >
            <Box
              sx={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                px: { xs: 2, md: 5 },
                pt: { xs: 7, md: 10 },
                pb: { xs: 2.5, md: 6 },
                boxSizing: 'border-box',
                maxWidth: '1500px',
                mx: 'auto',
              }}
            >
              {/* Liens de navigation, au-dessus des fresques. */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 3, md: 5 },
                  mb: { xs: 4.5, md: 5 },
                }}
              >
                {LINKS.map((link) => (
                  <Typography
                    key={link.to}
                    variant="h5"
                    onClick={() => goTo(link.to)}
                    sx={{
                      fontFamily: 'Manrope',
                      fontWeight: 400,
                      fontSize: { xs: '1.3rem', md: '1.35rem' },
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.6 },
                      transition: 'opacity 0.25s ease',
                    }}
                  >
                    {t(link.labelKey)}
                  </Typography>
                ))}

                <Typography
                  variant="h5"
                  onClick={() => {
                    window.location.href = 'mailto:hey@kajikaaki.com';
                    setMenuOpen(false);
                  }}
                  sx={{
                    fontFamily: 'Manrope',
                    fontWeight: 400,
                    fontSize: { xs: '1.3rem', md: '1.35rem' },
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.6 },
                    transition: 'opacity 0.25s ease',
                  }}
                >
                  {t('contact')}
                </Typography>
              </Box>

              {/* Les deux fresques, en grand : c'est l'entree principale. */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: { xs: 4, md: 3 },
                }}
              >
                {FRESCO_CARDS.map((card) => (
                  <Box
                    key={card.to}
                    onClick={() => goTo(card.to)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') goTo(card.to);
                    }}
                    sx={{
                      cursor: 'pointer',
                      outline: 'none',
                      '&:hover img, &:focus-visible img': { transform: 'scale(1.03)' },
                    }}
                  >
                    <Box
                      sx={{
                        overflow: 'hidden',
                        backgroundColor: '#f5f5f5',
                        // Mobile : bandeau panoramique, proche de la forme
                        // reelle des fresques (10 m sur 1,5 m) et qui laisse
                        // respirer la page. Desktop : cadre plus genereux.
                        aspectRatio: { xs: '21 / 9', md: '4 / 3' },
                        maxHeight: {
                          xs: cardMaxHeight ? `${cardMaxHeight}px` : 'none',
                          md: 'none',
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={card.image}
                        alt={t(card.altKey)}
                        loading="lazy"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          transition: 'transform 0.6s cubic-bezier(0.22, 0.61, 0.36, 1)',
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        mt: 1.4,
                        // Manrope, comme les liens de navigation juste au-dessus.
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 400,
                        fontSize: { xs: '1.05rem', md: '1.3rem' },
                        lineHeight: 1.25,
                      }}
                    >
                      {t(card.titleKey)}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 300,
                        fontSize: { xs: '0.8rem', md: '0.85rem' },
                        opacity: 0.7,
                      }}
                    >
                      {t(card.subtitleKey)}
                    </Typography>
                  </Box>
                ))}
              </Box>

            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
